import { applicationLocalStorage } from '@application/application_storage_local';
import { DateService } from '@application/time/date.service';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { Injectable, Scopes } from '@visiba/instantiation';
import type { Dayjs } from 'dayjs';

@Injectable({
	scope: Scopes.Scoped,
})
export class ViewBookingSettingsService {
	private receptionId: number;
	private defaultSettings: BookingSettings = {
		redRobinSessionId: null,
		representativeNin: null,
		attachFiles: true,
		lockFilters: false,
		descriptionOfMatter: null,
		reasonForVisit: null,
		locationType: null,
		locationId: null,
		filterResourceId: null,
		filterSpecialityId: null,
		filterUnitId: null,
		fromDate: null,
		toDate: null,
		searchContextId: null,
	};

	constructor(
		private readonly universalConfig: UniversalConfig,
		private readonly unitService: UnitService,
		private readonly dateService: DateService,
	) {
		const unit = this.unitService.getUnitNodeInTree(this.universalConfig.selectedUnitId);
		this.receptionId = unit.receptionId;
	}

	public getSettings(): BookingSettings {
		const settings = applicationLocalStorage.get('bookingSettings') ?? {};
		const receptionSettings: Partial<BookingSettingsRaw> = settings[this.receptionId] ?? {};

		const data: BookingSettings = {
			...this.defaultSettings,
			...receptionSettings,
			fromDate: receptionSettings.fromDate ? this.dateService.newDate(receptionSettings.fromDate) : null,
			toDate: receptionSettings.toDate ? this.dateService.newDate(receptionSettings.toDate) : null,
		};

		return data;
	}

	public setSettings(flowSettings: Partial<BookingSettings>): void {
		const settings = applicationLocalStorage.get('bookingSettings') ?? {};

		settings[this.receptionId] = {
			...this.defaultSettings,
			...flowSettings,
			fromDate: flowSettings.fromDate?.format('YYYY-MM-DDTHH:mm:ss') ?? null,
			toDate: flowSettings.toDate?.format('YYYY-MM-DDTHH:mm:ss') ?? null,
		};

		applicationLocalStorage.set('bookingSettings', settings);
	}

	public clearSettings(): void {
		const settings = applicationLocalStorage.get('bookingSettings') ?? {};

		delete settings[this.receptionId];

		applicationLocalStorage.set('bookingSettings', settings);
	}
}

interface DeepLinkParams {
	dom: Nullable<string>;
	rfv: Nullable<number[]>;
	attachFiles: Nullable<boolean>;
	lockFilters: Nullable<boolean>;
	resourceId: Nullable<number>;
	specialityId: Nullable<number>;
	unitId: Nullable<number>;
	locationId: Nullable<number>;
	locationType: Nullable<string>;
	fromDate: Nullable<string>;
	toDate: Nullable<string>;
	searchContextId: Nullable<string>;
}

interface BookingSettingsCommon {
	descriptionOfMatter: Nullable<string>;
	reasonForVisit: Nullable<number[]>;
	redRobinSessionId: Nullable<string>;
	representativeNin: Nullable<string>;
	attachFiles: boolean;
	lockFilters: boolean;
	locationType: Nullable<string>;
	locationId: Nullable<number>;
	filterResourceId: Nullable<number>;
	filterSpecialityId: Nullable<number>;
	filterUnitId: Nullable<number>;
	searchContextId: Nullable<string>;
}

export interface BookingSettingsRaw extends BookingSettingsCommon {
	fromDate: Nullable<string>;
	toDate: Nullable<string>;
}
export interface BookingSettings extends BookingSettingsCommon {
	fromDate: Nullable<Dayjs>;
	toDate: Nullable<Dayjs>;
}
